import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Interfaces for our data
interface Term { id: number; name: string; is_active: boolean; academic_year_name: string; }
interface Assessment { id: number; assessment_type_name: string; max_marks: number; weight: number; term: number; }
interface Subject { id: number; name: string; }
interface SchoolClass { id: number; name: string; }

interface MarkEntry {
    student_id: number;
    student_name: string;
    score: string; // Use string for input handling, convert to number for submission
    max_marks: number;
}

const EnterMarks: React.FC = () => {
    // 1. Selection State
    const [terms, setTerms] = useState<Term[]>([]);
    const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

    const [classes, setClasses] = useState<SchoolClass[]>([]);
    const [selectedClass, setSelectedClass] = useState<number | null>(null);

    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [selectedAssessment, setSelectedAssessment] = useState<number | null>(null);
    const [currentAssessmentDetails, setCurrentAssessmentDetails] = useState<Assessment | null>(null);

    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<number | null>(null);

    // 2. Data Entry State
    const [marksData, setMarksData] = useState<MarkEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Initial Load: Fetch Terms containing Active Term
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const res = await axios.get('/exams/terms/');
                // Safeguard: Ensure res.data is an array
                const termsData = Array.isArray(res.data) ? res.data : [];
                setTerms(termsData);

                // Auto-select active term
                if (termsData.length > 0) {
                    const active = termsData.find((t: Term) => t.is_active);
                    if (active) setSelectedTerm(active.id);
                }
            } catch (err) {
                toast.error("Failed to load terms");
                setTerms([]);
            }
        };
        fetchTerms();

        // Fetch Subjects once
        const fetchSubjects = async () => {
            try {
                const res = await axios.get('/exams/subjects/');
                setSubjects(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error(err);
                setSubjects([]);
            }
        };
        fetchSubjects();

        // Fetch Classes logic
        const fetchClasses = async () => {
            try {
                // 1. Fetch Current User to check Role & Assigned Classes
                let userRole = '';
                let assignedClassIds: number[] = [];

                try {
                    const userRes = await axios.get('/users/user');
                    const userData = userRes.data.user; // Assuming structure based on views.py
                    userRole = userData.role;
                    if (userData.assigned_classes) {
                        assignedClassIds = userData.assigned_classes.map((c: any) => c.id);
                    }
                } catch (uErr) {
                    console.error("Failed to fetch user info", uErr);
                    // Fallback to local storage if API fails, but can't filter strictly without API
                    userRole = localStorage.getItem('role') || '';
                }

                // 2. Fetch All Classes
                const res = await axios.get('/Branches/classes/');
                let classList: SchoolClass[] = [];

                if (Array.isArray(res.data)) {
                    classList = res.data;
                } else if (res.data && Array.isArray(res.data.results)) {
                    classList = res.data.results;
                } else if (res.data && Array.isArray(res.data.classes)) {
                    classList = res.data.classes;
                }

                // 3. Filter if Teacher
                if (userRole === 'teacher' && assignedClassIds.length > 0) {
                    classList = classList.filter(c => assignedClassIds.includes(c.id));
                } else if (userRole === 'teacher' && assignedClassIds.length === 0) {
                    // Teacher with no assigned classes
                    classList = [];
                    toast.warn("You are not assigned to any classes.");
                }

                setClasses(classList);

            } catch (err) {
                console.error("Failed to fetch classes", err);
                setClasses([]); // Fallback to empty array
            }
        };
        fetchClasses();

    }, []);

    // Load Assessments when Term changes
    useEffect(() => {
        if (!selectedTerm) return;
        const fetchAssessments = async () => {
            try {
                const res = await axios.get(`/exams/term-assessments/?term_id=${selectedTerm}`);
                setAssessments(Array.isArray(res.data) ? res.data : []);
                setSelectedAssessment(null); // Reset selection
                setMarksData([]);
            } catch (err) {
                toast.error("Failed to load assessments");
                setAssessments([]);
            }
        };
        fetchAssessments();
    }, [selectedTerm]);

    // Update details helper when assessment changes
    useEffect(() => {
        if (selectedAssessment && Array.isArray(assessments)) {
            const found = assessments.find(a => a.id === Number(selectedAssessment));
            setCurrentAssessmentDetails(found || null);
        } else {
            setCurrentAssessmentDetails(null);
        }
    }, [selectedAssessment, assessments]);

    // 3. Main Data Fetch: When Subject & Assessment & Class are Selected
    const fetchMarksSheet = async () => {
        if (!selectedSubject || !selectedAssessment || !selectedClass) {
            toast.warn("Please select Class, Subject and Assessment Type");
            return;
        }
        setLoading(true);
        try {
            // A. Fetch Students Filtered by Class
            const studentsRes = await axios.get(`/Branches/students/?class_id=${selectedClass}`);

            // Handle pagination/structure differences
            let studentList: any[] = [];
            if (Array.isArray(studentsRes.data)) {
                studentList = studentsRes.data;
            } else if (studentsRes.data && Array.isArray(studentsRes.data.results)) {
                studentList = studentsRes.data.results;
            } else if (studentsRes.data && Array.isArray(studentsRes.data.students)) {
                studentList = studentsRes.data.students;
            }

            // B. Fetch Existing Marks for this Assessment & Subject
            const marksRes = await axios.get(`/exams/assessment-scores/?term_assessment_id=${selectedAssessment}&subject_id=${selectedSubject}`);
            const existingMarks = Array.isArray(marksRes.data) ? marksRes.data : [];

            // C. Merge Logic
            const mergedData: MarkEntry[] = studentList.map((student: any) => {
                const foundMark = existingMarks.find((m: any) => m.student === student.id);
                return {
                    student_id: student.id,
                    student_name: student.name || `${student.first_name} ${student.last_name}`,
                    score: foundMark ? foundMark.score.toString() : '',
                    max_marks: currentAssessmentDetails?.max_marks || 100
                };
            });

            if (mergedData.length === 0) toast.info("No students found in this class.");

            setMarksData(mergedData);
        } catch (err) {
            console.error(err);
            toast.error("Failed to generate marks sheet. Check network.");
        } finally {
            setLoading(false);
        }
    };

    // 4. Handle Input Change with Validation
    const handleScoreChange = (studentId: number, value: string) => {
        // Allow empty string
        if (value === '') {
            updateMarkState(studentId, value);
            return;
        }

        const numVal = parseFloat(value);
        if (isNaN(numVal)) return; // Reject non-numbers

        const max = currentAssessmentDetails?.max_marks || 100;

        if (numVal < 0) {
            toast.warn(`Score cannot be negative`);
            return;
        }
        if (numVal > max) {
            toast.warn(`Max marks for this assessment is ${max}`);
            return; // Reject if > max
        }

        updateMarkState(studentId, value);
    };

    const updateMarkState = (studentId: number, value: string) => {
        setMarksData(prev => prev.map(item =>
            item.student_id === studentId ? { ...item, score: value } : item
        ));
    };

    // 5. Submit Handler
    const handleSave = async () => {
        if (!selectedTerm || !selectedSubject || !selectedAssessment) return;
        setSaving(true);

        // Filter only entered marks
        const payload = marksData
            .filter(m => m.score !== '')
            .map(m => ({
                student_id: m.student_id,
                subject_id: selectedSubject,
                term_assessment_id: selectedAssessment,
                score: parseFloat(m.score)
            }));

        try {
            await axios.post('/exams/assessment-scores/bulk_update_marks/', payload);
            toast.success("Marks saved successfully!");
        } catch (err) {
            toast.error("Failed to save marks");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="dashboard-content">
            <ToastContainer />

            <h2 className="fw-bold text-dark mb-4">Enter Marks</h2>

            {/* Selection Panel */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                    <div className="row g-3">

                        <div className="col-md-3">
                            <label className="form-label fw-bold small text-secondary">Term</label>
                            <select className="form-select" value={selectedTerm || ''} onChange={e => setSelectedTerm(Number(e.target.value))}>
                                <option value="">Select Term...</option>
                                {/* SAFER MAP: Use ?. chaining to prevent undefined access */}
                                {terms?.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({t.academic_year_name}) {t.is_active ? '✅' : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-bold small text-secondary">Class</label>
                            <select className="form-select" value={selectedClass || ''} onChange={e => setSelectedClass(Number(e.target.value))}>
                                <option value="">Select Class...</option>
                                {/* SAFER MAP */}
                                {classes?.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-bold small text-secondary">Assessment Type</label>
                            <div className="input-group">
                                <select className="form-select" value={selectedAssessment || ''} onChange={e => setSelectedAssessment(Number(e.target.value))} disabled={!selectedTerm}>
                                    <option value="">Select Assessment...</option>
                                    {assessments?.map(a => (
                                        <option key={a.id} value={a.id}>{a.assessment_type_name} (Max: {a.max_marks})</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-bold small text-secondary">Subject</label>
                            <select className="form-select" value={selectedSubject || ''} onChange={e => setSelectedSubject(Number(e.target.value))}>
                                <option value="">Select Subject...</option>
                                {subjects?.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-12 d-flex justify-content-end mt-4">
                            <button className="btn btn-primary px-5 fw-bold" onClick={fetchMarksSheet} disabled={loading || !selectedSubject || !selectedAssessment || !selectedClass}>
                                {loading ? 'Loading...' : 'Load Student List'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Marks Grid */}
            {/* SAFER CHECK for marksData */}
            {marksData && marksData.length > 0 && (
                <div className="card shadow border-0">
                    <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold">
                            Marks Entry Details
                            <span className="badge bg-info-subtle text-info ms-2">Max: {currentAssessmentDetails?.max_marks}</span>
                        </h6>
                        <button className="btn btn-success fw-bold px-4" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save All Marks'}
                        </button>
                    </div>
                    <div className="card-body p-0 table-responsive">
                        <table className="table table-striped table-hover mb-0 align-middle">
                            <thead className="bg-light text-uppercase small fw-bold">
                                <tr>
                                    <th className="ps-4">Student Name</th>
                                    <th style={{ width: '200px' }}>Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {marksData.map((entry) => (
                                    <tr key={entry.student_id}>
                                        <td className="ps-4 fw-medium">{entry.student_name}</td>
                                        <td>
                                            <input
                                                type="number"
                                                className={`form-control ${parseFloat(entry.score) > entry.max_marks ? 'is-invalid' : ''}`}
                                                placeholder={`0 - ${entry.max_marks}`}
                                                value={entry.score}
                                                onChange={(e) => handleScoreChange(entry.student_id, e.target.value)}
                                                min="0"
                                                max={entry.max_marks}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer bg-white text-end py-3">
                        <button className="btn btn-success fw-bold px-4" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save All Marks'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnterMarks;
